import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, PlayerProvider, useAuth } from './contexts/AppContext';
import { FeatureFlagProvider, useFeatureFlags } from './contexts/FeatureFlagContext';
import { Sidebar, BottomNav } from './components/Navigation';
import { MiniPlayer } from './components/Player';
import { LoginPage, RegisterPage, ForgotPasswordPage, ResetPasswordPage } from './pages/AuthPages';
import { HomePage } from './pages/HomePage';
import { DiscoverPage } from './pages/DiscoverPage';
import { ArtistsPage } from './pages/ArtistsPage';
import { ToolsPage } from './pages/ToolsPage';
import { ToolDetailPage } from './pages/ToolDetailPage';
import { FavoritesPage } from './pages/FavoritesPage';
import { PlaylistsPage } from './pages/PlaylistsPage';
import { ProfilePage } from './pages/ProfilePage';
import { StudioPage } from './pages/StudioPage';
import { CreatorDashboardPage } from './pages/CreatorDashboardPage';
import { AnalyticsPage } from './pages/AnalyticsPage'; // eslint-disable-line no-unused-vars  -- legacy admin analytics page, kept around in case we want it back. /analytics route now redirects to /admin.
import { ArtistAnalyticsPage } from './pages/ArtistAnalyticsPage'; // eslint-disable-line no-unused-vars

import { PricingPage, PaymentSuccessPage, PaymentCancelPage } from './pages/PaymentPages';
import { LibraryPage } from './pages/LibraryPage';
import { VideoDetailPage } from './pages/VideoDetailPage';
import { CreatorProfilePage } from './pages/CreatorProfilePage';
import { ProfileSetupPage } from './pages/ProfileSetupPage';
import { PulsePage } from './pages/PulsePage';
import { FilmsPage } from './pages/FilmsPage';
import { FilmDetailPage } from './pages/FilmDetailPage';
// AI-agent integration disabled "for now" (imports kept for easy re-enable)
// eslint-disable-next-line no-unused-vars
import { AgentsPage } from './pages/AgentsPage';
// eslint-disable-next-line no-unused-vars
import { DeveloperDocsPage } from './pages/DeveloperDocsPage';
import LiveChannel from './pages/LiveChannel';
import { AdminPage } from './pages/AdminPage';
import { AccessGatePage } from './pages/AccessGatePage';
import { InvitePage } from './pages/InvitePage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import TermsPage from './pages/TermsPage';
import AboutPage from './pages/AboutPage';
import { TrendingVideosPage, LatestVideosPage } from './pages/DiscoverListingPage';
// BecomeCreatorPage removed in iter 123 — the whole creator-approval
// flow was deleted. Anyone signed in can go straight to /studio.
import NotificationsPage from './pages/NotificationsPage';
import { AuthPromptProvider } from './components/AuthPromptModal';
import { ShareDialog } from './components/ShareDialog';
import { VisitTracker } from './components/VisitTracker';
import { InstallPrompt } from './components/InstallPrompt';
import { SiteFooter } from './components/SiteFooter';
import { LoadingScreen } from './components/LoadingScreen';
import { NotificationBell } from './components/NotificationBell';
import '@/index.css';

const API = process.env.REACT_APP_BACKEND_URL;

// Catch-all route that checks if path is an invite code
const InviteCodeCatcher = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [checking, setChecking] = React.useState(true);

  React.useEffect(() => {
    const checkCode = async () => {
      try {
        const res = await fetch(`${API}/api/access/check/${code.toUpperCase()}`);
        const data = await res.json();
        if (data.valid) {
          // Valid invite code - redirect to invite page
          navigate(`/i/${code}`, { replace: true });
        } else {
          // Not a valid code - go home
          navigate('/', { replace: true });
        }
      } catch {
        navigate('/', { replace: true });
      } finally {
        setChecking(false);
      }
    };
    checkCode();
  }, [code, navigate]);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#000' }}>
        <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  return null;
};

// Helper: build /login URL that returns the user to their current page
// after successful auth. Critical so deep-linked gated pages (e.g.
// /library, /playlists, /favorites) don't drop the user on / after login.
const loginWithNext = () => {
  const path = window.location.pathname + window.location.search;
  if (path && path !== '/login' && path !== '/') {
    return `/login?next=${encodeURIComponent(path)}`;
  }
  return '/login';
};

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#050505' }}>
        <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to={loginWithNext()} replace />;
  }
  
  return children;
};

/**
 * Discover-specific guard.
 *
 * Per the Feb 2026 spec, logged-out users must NOT see, reach, or use
 * Discover at all — and unlike the normal ProtectedRoute (which sends them
 * to /login so they can sign in and come back), this guard redirects them
 * to Home. Discover should feel like a feature that simply doesn't exist
 * for visitors, not a locked door asking for credentials.
 *
 * Renders a brief spinner during the auth bootstrap so there's no flash of
 * the Discover content before the redirect fires.
 */
const DiscoverGuard = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#050505' }}>
        <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/" replace />;
  }
  return children;
};

// Artist-only Route component
const ArtistRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#050505' }}>
        <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to={loginWithNext()} replace />;
  }
  
  // Invite code gate temporarily disabled — all users have access
  // if (!user.has_access) {
  //   return <Navigate to="/access-gate" replace />;
  // }
  
  // Email verification temporarily disabled
  // if (!user.email_verified) {
  //   return <EmailVerificationGate />;
  // }
  
  // Iter 123: "Become a Creator" concept removed. Anyone signed in can hit
  // Studio and upload; the backend auto-promotes their role to `artist` on
  // first upload (server.py — see auto-promote block in the presign +
  // chunked-upload handlers). No client-side gate needed anymore.

  return children;
};

// Access Gate Route - requires email verification
const AccessGatedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#050505' }}>
        <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to={loginWithNext()} replace />;
  }
  
  // Email verification temporarily disabled until domain is configured in Resend
  // if (!user.email_verified) {
  //   return <EmailVerificationGate />;
  // }
  
  return children;
};

// Layout component
const Layout = ({ children }) => {
  const { user } = useAuth();
  return (
    <div className="min-h-screen" style={{ background: '#050505' }}>
      <Sidebar />
      {/* Mobile-only floating notification bell — desktop has it in the Sidebar nav.
          Stays out of the BottomNav so existing 5-slot layout isn't disturbed. */}
      {user && (
        <div className="lg:hidden fixed top-3 right-3 z-50">
          <div className="rounded-full bg-black/70 backdrop-blur-md border border-zinc-800">
            <NotificationBell variant="compact" />
          </div>
        </div>
      )}
      <main className="lg:ml-64 min-h-screen pb-20 lg:pb-24">
        {children}
        <SiteFooter />
      </main>
      <BottomNav />
      <MiniPlayer />
    </div>
  );
};

// Smart Analytics route: admins see platform-wide analytics, artists are redirected to
// /studio (where their analytics is now embedded as a tab).
// Admins go to /admin so there is only ONE analytics surface.
const ADMIN_EMAILS_FE = ['modtest@aivue.com', 'admin@audivue.com', 'cpowersv@gmail.com'];
const SmartAnalytics = () => {
  const { user } = useAuth();
  const isAdmin = user && ADMIN_EMAILS_FE.includes(user.email);
  return <Navigate to={isAdmin ? '/admin' : '/studio'} replace />;
};

// Forward Google sitelinks-search-box hits (/search?q=...) to /discover?q=...
const SearchRedirect = () => {
  const search = window.location.search || '';
  return <Navigate to={`/discover${search}`} replace />;
};

// App Routes
const AppRoutes = () => {
  const { loading, user } = useAuth();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  return (
    <>
      <VisitTracker />
      <Routes>
      {/* Auth routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/profile-setup" element={<ProfileSetupPage />} />
      
      {/* Invite URL - short format: domain.com/j/CODE */}
      <Route path="/j/:code" element={<InvitePage />} />
      
      {/* Email Verification */}
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      
      {/* Access Gate */}
      <Route 
        path="/access-gate" 
        element={
          <ProtectedRoute>
            {user?.has_access ? <Navigate to="/" replace /> : <AccessGatePage />}
          </ProtectedRoute>
        } 
      />
      
      {/* Main app routes - PUBLIC (no login required) */}
      <Route path="/" element={<Layout><HomePage /></Layout>} />
      {/* 24/7 live channel — full-screen, no Layout wrapper so the video
          takes the entire viewport (no sidebar, no bottom nav intruding). */}
      <Route path="/live" element={<LiveChannel />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/about" element={<Layout><AboutPage /></Layout>} />
      {/* /become-a-creator legacy redirect (iter 123) — the flow is
          gone; anyone signed in can just hit /studio and upload. */}
      <Route path="/become-a-creator" element={<Navigate to="/studio" replace />} />
      <Route path="/discover" element={<DiscoverGuard><Layout><DiscoverPage /></Layout></DiscoverGuard>} />
      <Route path="/discover/trending" element={<Layout><TrendingVideosPage /></Layout>} />
      <Route path="/discover/latest" element={<Layout><LatestVideosPage /></Layout>} />
      <Route path="/artists" element={<Layout><ArtistsPage /></Layout>} />
      <Route path="/tools" element={<Layout><ToolsPage /></Layout>} />
      <Route path="/tools/:toolKey" element={<Layout><ToolDetailPage /></Layout>} />
      <Route path="/made-with/:toolKey" element={<Layout><ToolDetailPage /></Layout>} />
      <Route path="/pulse" element={<PulsePage />} />
      <Route path="/video/:id" element={<Layout><VideoDetailPage /></Layout>} />
      <Route path="/creator/:creatorId" element={<Layout><CreatorProfilePage /></Layout>} />
      
      {/* PROTECTED routes (login required) */}
      <Route path="/library" element={<AccessGatedRoute><Layout><LibraryPage /></Layout></AccessGatedRoute>} />
      <Route path="/admin" element={<AccessGatedRoute><Layout><AdminPage /></Layout></AccessGatedRoute>} />
      <Route path="/notifications" element={<AccessGatedRoute><Layout><NotificationsPage /></Layout></AccessGatedRoute>} />
      
      <Route
        path="/favorites"
        element={
          <AccessGatedRoute>
            <Layout><FavoritesPage /></Layout>
          </AccessGatedRoute>
        }
      />
      
      <Route
        path="/playlists"
        element={
          <AccessGatedRoute>
            <Layout><PlaylistsPage /></Layout>
          </AccessGatedRoute>
        }
      />
      
      <Route
        path="/studio"
        element={
          <AccessGatedRoute>
            <Layout><StudioPage /></Layout>
          </AccessGatedRoute>
        }
      />
      
      {/* Films (public browse + detail) */}
      <Route path="/films" element={<Layout><FilmsPage /></Layout>} />
      <Route path="/films/:id" element={<Layout><FilmDetailPage /></Layout>} />
      {/* AI-agent integration disabled "for now" — routes removed so the pages
          are unreachable. Code + imports kept; re-add these two routes to re-enable.
      <Route path="/developers" element={<Layout><DeveloperDocsPage /></Layout>} />
      <Route path="/studio/agents" element={<AccessGatedRoute><Layout><AgentsPage /></Layout></AccessGatedRoute>} />
      */}

      {/* Legacy redirect */}
      <Route path="/upload" element={<Navigate to="/studio" replace />} />
      <Route path="/my-videos" element={<Navigate to="/studio" replace />} />
      {/* SEO/Schema search target → forwards to Discover preserving the ?q= query */}
      <Route path="/search" element={<SearchRedirect />} />
      
      <Route
        path="/creator"
        element={
          <ArtistRoute>
            <Layout><CreatorDashboardPage /></Layout>
          </ArtistRoute>
        }
      />
      
      <Route
        path="/profile"
        element={
          <AccessGatedRoute>
            <Layout><ProfilePage /></Layout>
          </AccessGatedRoute>
        }
      />
      
      {/* Payment routes */}
      <Route path="/pricing" element={<AccessGatedRoute><Layout><PricingPage /></Layout></AccessGatedRoute>} />
      <Route path="/analytics" element={<ArtistRoute><Layout><SmartAnalytics /></Layout></ArtistRoute>} />
      <Route
        path="/payment/success"
        element={
          <AccessGatedRoute>
            <PaymentSuccessPage />
          </AccessGatedRoute>
        }
      />
      <Route path="/payment/cancel" element={<PaymentCancelPage />} />
      
      {/* Catch all - go home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <FeatureFlagProvider>
          <PlayerProvider>
            <AuthPromptProvider>
              <AppRoutes />
              <InstallPrompt />
              <ShareDialog />
              <Toaster
                position="top-center"
                toastOptions={{
                  style: {
                    background: '#1A1A1A',
                    color: '#fff',
                    border: '1px solid #27272A',
                  },
                }}
              />
            </AuthPromptProvider>
          </PlayerProvider>
        </FeatureFlagProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
